import { useContext, useEffect, useMemo, useRef, useState } from "react";
import useUserLocation from "../../hooks/useUserLocation";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchFreelancerJobs, fetchJobsCount, findJobs } from "../../services/api/workOrder";
import FindJobTable from "./FindJobTable";
import FindJobListingHeader from "./FindJobListingHeader";
import { debounce } from "../../utils/helpers";
import BaseLoader from "../../components/ui/BaseLoader";
import { setJobEngineerFilters } from "../../store/filters/filtersSlice";
import { useDispatch, useSelector } from "react-redux";
import { useWorkOrderSocket } from "../../hooks/useWorkOrderSocket";
import { LayoutContext } from "../../context/LayoutContext";
import {
  fetchAllSubCategories,
  fetchAllVendors,
  fetchCategories,
} from "../../services/api/category";
import { PaymentType } from "../../utils/enum";
import useAuth from "../../hooks/useAuth";
import { useCurrentUser } from "../../hooks/useCurrentUser";

const FindJob = () => {
  const { logout } = useAuth();
  const { userData } = useCurrentUser();
  const isCompanyMember = !!userData?.isCompanyMember;
  const { isBannerVisible } = useContext(LayoutContext);
  const { jobs, isAvailableJobsLoading, jobsFilters, setJobsFilters } = useWorkOrderSocket();
  const engineerFilters = useSelector((state) => state.filters.value);
  const [freelancerJobs, setFreelancerJobs] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filtersChanged, setFiltersChanged] = useState(false);

  const dispatch = useDispatch();

  const { userCurrentLocation, isLoading: isLocationLoading } = useUserLocation();
  const { data: categoriesData } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });
  const { data: vendorsData } = useQuery({
    queryKey: ["vendors"],
    queryFn: fetchAllVendors,
  });
  const { data: subCategoriesData } = useQuery({
    queryKey: ["subCategories"],
    queryFn: fetchAllSubCategories,
  });
  const categories = categoriesData?.data || [];
  const vendors = vendorsData?.data || [];
  const subCategories = subCategoriesData?.data || [];

  const appliedFilters = useMemo(() => {
    const filters = [];

    if (engineerFilters?.categoryId) {
      const name = categories.find((c) => c.id === engineerFilters.categoryId)?.category;
      if (name) filters.push({ prefix: "Technology Stack", value: name, key: "categoryId" });
    }

    if (engineerFilters?.vendorId) {
      const name = vendors.find((v) => v.id === engineerFilters.vendorId)?.name;
      if (name) filters.push({ prefix: "Category", value: name, key: "vendorId" });
    }

    if (engineerFilters?.subCategoryIds?.length > 0) {
      const items = subCategories
        .filter((s) => engineerFilters.subCategoryIds.includes(s.id))
        .map((s) => ({ id: s.id, name: s.subCategory }));

      items.forEach(({ id, name }) =>
        filters.push({
          prefix: "Skill",
          value: name,
          key: "subCategoryIds",
          id,
        }),
      );
    }

    if (engineerFilters?.minRate || engineerFilters?.maxRate) {
      const min = Number(engineerFilters.minRate ?? 0);
      const max = Number(engineerFilters.maxRate ?? 100);
      filters.push({
        prefix: "Rate",
        value: min === max ? `$${min}` : `$${min} - $${max}`,
        key: "rateRange",
      });
    }

    if (engineerFilters?.startDate || engineerFilters?.endDate) {
      filters.push({
        prefix: "Date",
        value: `${engineerFilters.startDate || "Any"} - ${engineerFilters.endDate || "Any"}`,
        key: "dateRange",
      });
    }

    if (engineerFilters?.paymentType) {
      const label = PaymentType[engineerFilters.paymentType] || engineerFilters.paymentType;
      filters.push({
        prefix: "Payment",
        value: label,
        key: "paymentType",
      });
    }

    if (engineerFilters?.minRequests !== "" || engineerFilters?.maxRequests !== "") {
      const min = engineerFilters.minRequests ?? 0;
      const max = engineerFilters.maxRequests ?? 0;

      filters.push({
        prefix: "Requests",
        value: min === max ? `${min}` : `${min} - ${max}`,
        key: "requestsRange",
      });
    }

    return filters;
  }, [engineerFilters, categories, vendors, subCategories]);

  const {
    isLoading: isJobsCountLoading,
    data: jobsCount,
    refetch: reFetchJobsCount,
  } = useQuery({
    queryKey: ["jobsCount"],
    queryFn: () =>
      fetchJobsCount({
        latitude:
          engineerFilters?.coverageAreaCoordinates?.latitude || userCurrentLocation?.latitude,
        longitude:
          engineerFilters?.coverageAreaCoordinates?.longitude || userCurrentLocation?.longitude,
        radius: engineerFilters?.coverageAreaRadius,
        categoryId: engineerFilters?.categoryId,
        vendorId: engineerFilters?.vendorId,
        subCategoryIds: engineerFilters?.subCategoryIds,
        minRate: engineerFilters?.minRate,
        maxRate: engineerFilters?.maxRate,
        minRequests: engineerFilters.minRequests,
        maxRequests: engineerFilters.maxRequests,
        paymentType: engineerFilters?.paymentType,
        startDate: engineerFilters?.startDate,
        endDate: engineerFilters?.endDate,
        search: engineerFilters.search || "",
        isCoverageApplied: !!engineerFilters?.coverageAreaSearchText,
      }),
    // enabled: !!engineerFilters?.coverageAreaCoordinates?.latitude,
    // enabled: false,
  });

  const {
    isLoading: isWorkOrderLoading,
    error: isWorkOrderError,
    data: jobsData,
    refetch: fetchJobs,
  } = useQuery({
    queryKey: [
      "findJobs",
      {
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        search: debouncedSearch,
        startDate: engineerFilters.startDate,
        endDate: engineerFilters.endDate,
        minRate: engineerFilters.minRate,
        maxRate: engineerFilters.maxRate,
        minRequests: engineerFilters.minRequests,
        maxRequests: engineerFilters.maxRequests,
        vendorId: engineerFilters.vendorId,
        categoryId: engineerFilters.categoryId,
        subCategoryIds: engineerFilters.subCategoryIds,
        paymentType: engineerFilters.paymentType,
      },
    ],
    queryFn: () =>
      findJobs({
        latitude: engineerFilters?.coverageAreaCoordinates?.latitude,
        longitude: engineerFilters?.coverageAreaCoordinates?.longitude,
        radius: engineerFilters?.coverageAreaRadius,
        page: engineerFilters?.pagination?.page + 1,
        limit: engineerFilters?.pagination?.pageSize,
        search: engineerFilters.search || "",
        startDate: engineerFilters.startDate,
        endDate: engineerFilters.endDate,
        minRate: engineerFilters.minRate,
        maxRate: engineerFilters.maxRate,
        minRequests: engineerFilters.minRequests,
        maxRequests: engineerFilters.maxRequests,
        categoryId: engineerFilters.categoryId,
        vendorId: engineerFilters.vendorId,
        subCategoryIds: engineerFilters.subCategoryIds,
        paymentType: engineerFilters.paymentType,
        isCoverageApplied: !!engineerFilters?.coverageAreaSearchText,
      }),
    enabled: false,
  });

  const {
    isLoading: isFreelancerJobsLoading,
    error: isFreelancerJobsError,
    data: freealncerjobsData,
    mutateAsync: fetchFreelancerJobsMutation,
  } = useMutation({
    mutationFn: fetchFreelancerJobs,
    enabled: false,
  });

  if (isWorkOrderError?.response?.status === 401) {
    logout();
  }

  useEffect(() => {
    if (!isCompanyMember && jobsFilters.status === "AVAILABLE") {
      setFreelancerJobs(jobs || []);
      setRowCount(jobsData?.data?.totalCount);
      reFetchJobsCount();
    } else {
      fetchFreelancerJobsApi();
      reFetchJobsCount();
    }
    if (filtersChanged) {
      setFiltersChanged(false);
    }
  }, [
    jobsFilters,
    jobs,
    jobsData,
    paginationModel.page,
    paginationModel.pageSize,
    debouncedSearch,
    jobsFilters.status,
    filtersChanged,
  ]);
  useEffect(() => {
    if (!isJobsCountLoading && jobsCount?.data) {
      setRowCount(getJobTableRowCount(jobsCount.data, jobsFilters.status, jobsFilters.isCounter));

      setPaginationModel((prev) => ({
        ...prev,
        page: 0,
      }));
    }
  }, [jobsFilters.status, jobsCount, jobsFilters.isCounter]);

  useEffect(() => {
    // Determine the latitude and longitude to use
    const latitude =
      engineerFilters?.coverageAreaCoordinates?.latitude || userCurrentLocation?.latitude;
    const longitude =
      engineerFilters?.coverageAreaCoordinates?.longitude || userCurrentLocation?.longitude;

    const payload = {
      filterName: "coverageAreaCoordinates",
      data: {
        latitude: latitude,
        longitude: longitude,
      },
    };
    dispatch(setJobEngineerFilters(payload));
  }, [userCurrentLocation, jobsFilters.status]);

  const handleRadiusChange = (newRadius) => {
    const payload = {
      filterName: "coverageAreaRadius",
      data: newRadius,
    };
    dispatch(setJobEngineerFilters(payload));
  };
  const handleLocationSearch = (address) => {
    const payload = {
      filterName: "coverageAreaCoordinates",
      data: {
        latitude: address?.latitude,
        longitude: address?.longitude,
      },
    };

    dispatch(setJobEngineerFilters(payload));
    dispatch(
      setJobEngineerFilters({
        filterName: "coverageAreaSearchText",
        data: address.completeAddress,
      }),
    );
  };
  const handleRemoveFilter = (key, id) => {
    if (key === "rateRange") {
      dispatch(setJobEngineerFilters({ filterName: "minRate", data: "" }));
      dispatch(setJobEngineerFilters({ filterName: "maxRate", data: "" }));
    } else if (key === "requestsRange") {
      dispatch(setJobEngineerFilters({ filterName: "minRequests", data: "" }));
      dispatch(setJobEngineerFilters({ filterName: "maxRequests", data: "" }));
    } else if (key === "dateRange") {
      dispatch(setJobEngineerFilters({ filterName: "startDate", data: "" }));
      dispatch(setJobEngineerFilters({ filterName: "endDate", data: "" }));
    } else if (key === "subCategoryIds") {
      dispatch(
        setJobEngineerFilters({
          filterName: "subCategoryIds",
          data: engineerFilters.subCategoryIds.filter((subId) => subId !== id),
        }),
      );
    } else if (key === "vendorId") {
      dispatch(setJobEngineerFilters({ filterName: "vendorId", data: "" }));
      dispatch(setJobEngineerFilters({ filterName: "subCategoryIds", data: [] }));
    } else if (key === "categoryId") {
      dispatch(setJobEngineerFilters({ filterName: "categoryId", data: "" }));
      dispatch(setJobEngineerFilters({ filterName: "vendorId", data: "" }));
      dispatch(setJobEngineerFilters({ filterName: "subCategoryIds", data: [] }));
    } else {
      dispatch(setJobEngineerFilters({ filterName: key, data: "" }));
    }

    dispatch(
      setJobEngineerFilters({
        filterName: "pagination",
        data: { page: 0, pageSize: engineerFilters.pagination.pageSize },
      }),
    );

    setFiltersChanged(true);
  };

  const fetchFreelancerJobsApi = async () => {
    const response = await fetchFreelancerJobsMutation({
      ...jobsFilters,
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      search: searchInput,
      latitude: engineerFilters?.coverageAreaCoordinates?.latitude,
      longitude: engineerFilters?.coverageAreaCoordinates?.longitude,
      radius: engineerFilters?.coverageAreaRadius,
      startDate: engineerFilters?.startDate,
      endDate: engineerFilters?.endDate,
      minRate: engineerFilters?.minRate,
      maxRate: engineerFilters?.maxRate,
      minRequests: engineerFilters?.minRequests,
      maxRequests: engineerFilters?.maxRequests,
      categoryId: engineerFilters?.categoryId,
      vendorId: engineerFilters?.vendorId,
      subCategoryIds: engineerFilters?.subCategoryIds,
      paymentType: engineerFilters?.paymentType,
      status: jobsFilters.status,
      isCounter: jobsFilters.isCounter,
    });

    setFreelancerJobs(response?.data?.workOrdersWithDistance || []);
    setRowCount(response?.data?.totalCount);
  };
  const getJobTableRowCount = (data, status, isCounter) => {
    if (!data) return 0;

    if (status === "") {
      return data["ALL"];
    }

    if (status === "PENDING") return data[isCounter ? "COUNTERED" : "REQUESTED"] ?? 0;

    return data[status?.toUpperCase()] ?? 0;
  };

  const handlePaginationChange = (newModel) => {
    setPaginationModel(newModel);
    dispatch(
      setJobEngineerFilters({
        filterName: "pagination",
        data: {
          page: newModel.page,
          pageSize: newModel.pageSize,
        },
      }),
    );
  };
  const debouncedSearchRef = useRef(
    debounce((value) => {
      setDebouncedSearch(value);
      dispatch(setJobEngineerFilters({ filterName: "search", data: value }));
      dispatch(setJobEngineerFilters({ filterName: "pagination", data: { page: 0 } }));
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 500),
  );

  const handleSearchChange = (value) => {
    setSearchInput(value);
    debouncedSearchRef.current(value);
  };
  return (
    <>
      <BaseLoader
        isLoading={
          isLocationLoading ||
          (jobsFilters.status === "AVAILABLE" ? isAvailableJobsLoading : isFreelancerJobsLoading)
        }
      />
      <div
        className={`bg-[#F6F6F6] sm:fixed sticky 2xl:w-[95.2%] xl:w-[93.2%] lg:w-[90.6%] md:w-[87.5%] sm:w-[95%] w-full h-screen transition-all pt-4 ${
          isBannerVisible ? "top-[104px] sm:mt-0 mt-12" : "top-[56px]"
        }`}
      >
        <FindJobListingHeader
          radius={engineerFilters?.coverageAreaRadius}
          handleRadiusChange={handleRadiusChange}
          handleSearchChange={handleSearchChange}
          fetchJobs={fetchJobs}
          fetchFreelancerJobsMutation={fetchFreelancerJobsApi}
          reFetchJobsCount={reFetchJobsCount}
          appliedFilters={appliedFilters}
          removeFilter={handleRemoveFilter}
          currentStatus={jobsFilters.status}
          isWorkOrderLoading={isWorkOrderLoading}
          handleLocationSearch={handleLocationSearch}
          selectedLocation={engineerFilters?.coverageAreaSearchText}
          search={searchInput}
          setFiltersChanged={setFiltersChanged}
          isCompanyMember={isCompanyMember}
        />
        <FindJobTable
          jobs={freelancerJobs}
          setJobsFilters={setJobsFilters}
          jobsFilters={jobsFilters}
          jobsCount={jobsCount?.data}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationChange}
          rowCount={rowCount}
          search={searchInput}
        />
      </div>
    </>
  );
};

export default FindJob;
